import {
  Alert,
  AlertDialog,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  toast,
  type DataColumn,
} from '@1k5-tech-es/grado-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router';

import { api } from '../../api/client';
import type { AppUserRow, IdhubSync } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorBlock, LoadingBlock } from '../../components/ui';
import { describeApiError } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import {
  ACTION_LABEL,
  ACTION_PATCH,
  availableActions,
  displayName,
  groupByAccess,
  idhubNotice,
  NEEDS_CONFIRMATION,
  requestDetail,
  type UserAction,
} from './users';

const CONFIRM_TEXT: Partial<Record<UserAction, (name: string) => string>> = {
  deny: (name) => `¿Denegar el acceso a ${name}? No podrá ver ningún dato de la aplicación.`,
  revoke: (name) => `¿Retirar el acceso a ${name}? Dejará de ver los datos en su próxima acción.`,
  removeAdmin: (name) => `¿Quitar el rol de administrador a ${name}? Seguirá teniendo acceso a los datos.`,
};

const DONE_TEXT: Record<UserAction, (name: string) => string> = {
  approve: (name) => `${name} ya tiene acceso`,
  deny: (name) => `Acceso denegado a ${name}`,
  revoke: (name) => `Acceso retirado a ${name}`,
  makeAdmin: (name) => `${name} ahora es administrador`,
  removeAdmin: (name) => `${name} ya no es administrador`,
};

export const usersQueryKey = ['users'] as const;

interface Decision {
  user: AppUserRow;
  action: UserAction;
}

export function UsersPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: usersQueryKey,
    queryFn: () => api.get<{ users: AppUserRow[] }>('/api/users').then((r) => r.users),
    enabled: session.user.isAdmin,
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Decision | null>(null);
  const decide = useMutation({
    mutationFn: ({ user, action }: Decision) =>
      api.patch<{ user: AppUserRow; idhub: IdhubSync }>(`/api/users/${user.id}`, ACTION_PATCH[action]),
    onMutate: () => setNotice(null),
    onSuccess: (result, { user, action }) => {
      const warning = idhubNotice(action, result.idhub, displayName(user));
      if (warning) setNotice(warning);
      else toast.success(DONE_TEXT[action](displayName(user)));
      void queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
    onSettled: () => setConfirming(null),
  });

  if (!session.user.isAdmin) return <Navigate to="/datos" replace />;

  const run = (user: AppUserRow, action: UserAction) => {
    if (NEEDS_CONFIRMATION.has(action)) setConfirming({ user, action });
    else decide.mutate({ user, action });
  };
  const busyId = decide.isPending ? decide.variables?.user.id : undefined;

  return (
    <>
      <PageHeader
        title="Usuarios"
        subtitle="Quién puede ver los datos. Iniciar sesión con IDHub no basta: cada persona necesita la aprobación de un administrador de Performance."
      />
      {decide.isError && (
        <Alert tone="danger" title="No se ha guardado el cambio" className="mb-4" onDismiss={() => decide.reset()}>
          {describeApiError(decide.error, 'No se ha podido guardar el cambio.', {
            502: 'IDHub no ha confirmado la aprobación, así que no se ha cambiado nada. Inténtalo de nuevo en unos minutos.',
          })}
        </Alert>
      )}
      {notice && (
        <Alert tone="warning" title="Revisa también IDHub" className="mb-4" onDismiss={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {users.isPending ? (
        <LoadingBlock />
      ) : users.isError ? (
        <ErrorBlock error={users.error} />
      ) : (
        <UserSections users={users.data} myId={session.user.id} busyId={busyId} onAction={run} />
      )}
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && !decide.isPending && setConfirming(null)}
        title={confirming ? ACTION_LABEL[confirming.action] : ''}
        description={confirming ? CONFIRM_TEXT[confirming.action]?.(displayName(confirming.user)) : undefined}
        footer={
          <>
            <Button variant="secondary" disabled={decide.isPending} onClick={() => setConfirming(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" loading={decide.isPending} onClick={() => confirming && decide.mutate(confirming)}>
              {confirming ? ACTION_LABEL[confirming.action] : ''}
            </Button>
          </>
        }
      />
    </>
  );
}

function UserSections({
  users,
  myId,
  busyId,
  onAction,
}: {
  users: AppUserRow[];
  myId: number;
  busyId: number | undefined;
  onAction: (user: AppUserRow, action: UserAction) => void;
}) {
  const groups = groupByAccess(users);
  const actions = (user: AppUserRow) => (
    <ActionButtons user={user} actions={availableActions(user, myId)} busy={busyId === user.id} onAction={onAction} />
  );
  const columns: DataColumn<AppUserRow>[] = [
    { key: 'email', header: 'Persona', render: (user) => <Person user={user} you={user.id === myId} /> },
    { key: 'isAdmin', header: 'Rol', render: (user) => <RoleBadge user={user} /> },
    {
      key: 'lastLoginAt',
      header: 'Último acceso',
      render: (user) =>
        user.lastLoginAt ? (
          <span className="whitespace-nowrap tabular-nums">{formatDateTime(user.lastLoginAt)}</span>
        ) : (
          <span className="text-muted-foreground">Aún no ha entrado</span>
        ),
    },
    {
      key: 'decidedBy',
      header: 'Aprobado por',
      render: (user) =>
        user.fixedAdmin ? <span className="text-muted-foreground">Fijo (ADMIN_EMAILS)</span> : <DecisionText user={user} />,
    },
  ];

  return (
    <div className="space-y-6">
      <Card className={groups.pending.length ? 'border-warning-border' : undefined}>
        <CardHeader>
          <CardTitle as="h2">
            Pendientes de aprobar
            {groups.pending.length > 0 && <Badge tone="warning">{groups.pending.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {groups.pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay solicitudes pendientes.</p>
          ) : (
            <ul className="divide-y divide-border">
              {groups.pending.map((user) => (
                <li key={user.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <Person user={user} detail={requestDetail(user)} />
                  {actions(user)}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <DataTable
        caption={`Con acceso (${groups.approved.length})`}
        rows={groups.approved}
        columns={columns}
        rowKey={(user) => String(user.id)}
        pageSize={20}
        rowActions={actions}
      />

      {groups.denied.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle as="h2">Sin acceso ({groups.denied.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {groups.denied.map((user) => (
                <li key={user.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <Person user={user} detail={<DecisionText user={user} verb="Denegado" />} />
                  {actions(user)}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Person({ user, detail, you }: { user: AppUserRow; detail?: React.ReactNode; you?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 font-semibold">
        <span className="truncate">{displayName(user)}</span>
        {you && <Badge tone="brand">Tú</Badge>}
      </div>
      <div className="truncate text-xs text-muted-foreground">{user.email}</div>
      {detail && <div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>}
    </div>
  );
}

function RoleBadge({ user }: { user: AppUserRow }) {
  if (user.fixedAdmin) return <Badge tone="brand">Administrador fijo</Badge>;
  return user.isAdmin ? <Badge tone="brand">Administrador</Badge> : <Badge>Usuario</Badge>;
}

function DecisionText({ user, verb }: { user: AppUserRow; verb?: string }) {
  if (!user.decidedAt) return <span className="text-muted-foreground">—</span>;
  return (
    <span>
      {verb ? `${verb} por ` : ''}
      {user.decidedBy ?? 'un administrador'} · {formatDateTime(user.decidedAt)}
    </span>
  );
}

function ActionButtons({
  user,
  actions,
  busy,
  onAction,
}: {
  user: AppUserRow;
  actions: UserAction[];
  busy: boolean;
  onAction: (user: AppUserRow, action: UserAction) => void;
}) {
  if (actions.length === 0) return null;
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {actions.map((action) => (
        <Button
          key={action}
          size="sm"
          variant={action === 'approve' ? 'primary' : action === 'deny' || action === 'revoke' ? 'danger' : 'secondary'}
          disabled={busy}
          onClick={() => onAction(user, action)}
        >
          {ACTION_LABEL[action]}
        </Button>
      ))}
    </div>
  );
}
